// src/App.tsx
import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import './App.css';

function App() {
  return (
    <HelmetProvider>
      <Helmet defaultTitle="TwoBirds" titleTemplate="%s - TwoBirds" />

      {/* We now ONLY route to redirect components */}
      <Routes>
        <Route path="/verdvisir/view/:id" element={<RedirectToNewReport />} />
        <Route path="*" element={<RedirectToMainSite />} />
      </Routes>
    </HelmetProvider>
  );
}

/**
 * Redirects /verdvisir/view/:id to the new report service:
 *   https://report.aurbjorg.is/is/report/:id
 * preserving query params and hash.
 *
 * This is the core of the path mapping we're introducing.
 */
export function RedirectToNewReport() {
  const { id } = useParams();
  const location = useLocation();

  React.useEffect(() => {
    const search = location.search ?? '';
    const hash = location.hash ?? '';

    if (!id) {
      // This should never happen with the /verdvisir/view/:id route,
      // but we log defensively so we have breadcrumbs if something odd happens.
      console.error('[RedirectToNewReport] Missing ":id" route param; staying on page.', {
        pathname: location.pathname,
        search,
        hash,
      });
      return;
    }

    const targetUrl = `https://report.aurbjorg.is/is/report/${id}${search}${hash}`;

    console.log('[RedirectToNewReport] Redirecting to new report page', {
      id,
      targetUrl,
    });

    try {
      window.location.assign(targetUrl);
    } catch (error) {
      // Defensive logging: if the browser refuses navigation for any reason,
      // we'll have a clear error trail in the console.
      console.error('[RedirectToNewReport] Failed to redirect to new report page', {
        error,
        targetUrl,
      });
    }
  }, [id, location.pathname, location.search, location.hash]);

  // If JS is slow or something goes wrong, the user at least sees this.
  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
      Hleð upp verðmatsskýrslu. Hinkrið aðeins
    </div>
  );
}

/**
 * Catch-all: anything that isn't /verdvisir/view/:id goes to the main site.
 * This preserves the old behaviour for every other path.
 */
export function RedirectToMainSite() {
  const location = useLocation();

  React.useEffect(() => {
    const targetUrl = 'https://aurbjorg.is/';

    console.log('[RedirectToMainSite] Path not matched; redirecting to main site.', {
      from: location.pathname + location.search + location.hash,
      targetUrl,
    });

    try {
      window.location.assign(targetUrl);
    } catch (error) {
      console.error('[RedirectToMainSite] Failed to redirect to main site', {
        error,
        targetUrl,
      });
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
}

export default App;
